import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import JsQrReader from "./Components/JsQrReader";

import PureModal from "components/Modals/PureModal";
import { NoteIcon, QrCodeIcon, SearchIcon, TicketIcon } from "components/Icons";
import { getStorage, setStorage } from "store/local";
import proxy from "api/proxy";
import IntegerField from "./Components/IntegerField";
import SelectDevice from "apps/AccountStatement/SelectDevice";
import funcs from "tools/functions";
import StdButton from "components/Buttons/StdButton";
import { Alert } from "components/Alerts/Alert";
import InfoState from "./Components/InfoModal";

const fieldQuantity = {
  level: "wizard",
  name: "quantity",
  readOnly: false,
};
function checkDate(dateStr) {
  if (!dateStr) {
    return { state: false, mensaje: "Fecha no disponible" };
  }
  const [year, month, day] = dateStr.split("-").map(Number);
  const inputDate = new Date(Date.UTC(year, month - 1, day));
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (
    inputDate.getUTCFullYear() === today.getFullYear() &&
    inputDate.getUTCMonth() === today.getMonth() &&
    inputDate.getUTCDate() === today.getDate()
  ) {
    return { state: true, mensaje: "La fecha es hoy" };
  } else if (inputDate < today) {
    return { state: false, mensaje: "La fecha ya pasó" };
  } else {
    return { state: false, mensaje: "La fecha es futura" };
  }
}

function formatNumberCo(value) {
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
  }).format(value);
}

const ScreenSaleValidation = () => {
  const inputRef = useRef();
  const [ctxActivity, setCtxActivity] = useState(getStorage("saleActivity"));
  const session = getStorage("ctxSession");
  const [open, setOpen] = useState(false);
  const [openInfo, setOpenInfo] = useState(false);
  const [alertState, setAlertState] = useState({
    show: false,
    text: "app.sale.validation.alert_msg_default",
    color: "bg-blue-100",
  });
  const [sale, setSale] = useState(null);
  const [isToday, setIsToday] = useState({});
  const [markedProducts, setMarkedProducts] = useState([]);
  const [categoriesShop, setCategoriesShop] = useState([]);
  const [modalView, setModalView] = useState("scan");
  const [quantities, setQuantities] = useState({});

  const setQuantity = useCallback((lineId, value) => {
    setQuantities((prev) => ({ ...prev, [lineId]: value }));
  }, []);

  const focusInput = useCallback(() => {
    setTimeout(() => inputRef.current?.focus(), 250);
  }, []);

  const handleCurrentDevice = async () => {
    const _terminal = funcs.getCookie();
    const saleActivity = getStorage("saleActivity") || {};
    const updateSaleActivity = {};

    let sale_device = saleActivity.sale_device;
    let agent = saleActivity?.agent;
    let shop = { id: saleActivity.shop, name: saleActivity.shop_name };

    if (!sale_device) {
      sale_device = {
        id: parseInt(_terminal?.device_id),
        name: _terminal?.device_name,
      };

      if (_terminal?.shop_id !== saleActivity?.shop) {
        shop = {
          id: parseInt(_terminal?.shop_id),
          name: _terminal?.shop_name,
        };
        updateSaleActivity.sale_device = sale_device;
        updateSaleActivity.shop = parseInt(_terminal?.shop_id);
        updateSaleActivity.shop_name = _terminal?.shop_name;
      }
    }

    if (!agent) {
      const { data: agents } = await proxy.search(
        "commission.agent",
        [["user", "=", session.user]],
        ["rec_name"],
      );

      if (agents && agents.length > 0) {
        agent = agents[0];
        updateSaleActivity.agent = agent;
      }
    }

    if (Object.keys(updateSaleActivity).length > 0) {
      const updatedActivity = { ...saleActivity, ...updateSaleActivity };
      await setStorage("saleActivity", updatedActivity);
      await setStorage("ctxSession", {
        ...session,
        shop: parseInt(shop.id),
      });
      setCtxActivity(updatedActivity);
    }
  };

  const handleQrSearch = async (result, camera = false) => {
    const _result = camera ? result.text : result.target.value;
    if (!_result) return;

    const { data } = await proxy.search(
      "crm.opportunity",
      ["number", "=", _result],
      [
        "id",
        "number",
        "party.name",
        "party.id_number",
        "state",
        "lines.product.name",
        "lines.product.template.categories",
        "lines.product.template.products_mix.parent_product",
        "lines.product.categories",
        "lines.quantity",
        "lines.state_sale",
        "lines.unit_price",
        "lines.quantity_received",
        "total_amount",
        "start_date",
        "end_date",
        "description",
        "shop",
      ],
    );

    if (data?.length > 0) {
      const { data: dataInvoice } = await proxy.search(
        "electronic.payment",
        ["origin", "=", `crm.opportunity,${data[0]?.id}`],
        ["state", "checkout_id"],
      );
      setSale({
        ...data[0],
        state: dataInvoice[0]?.state,
        checkout: dataInvoice[0]?.checkout_id,
      });
      const _isToday = checkDate(data[0]?.end_date);
      setIsToday(_isToday);

      const saleValidation =
        dataInvoice[0]?.state != "succeeded" || !_isToday.state;
      setOpenInfo(saleValidation);

      setQuantities({});
      setMarkedProducts([]);
      setAlertState((prev) => ({ ...prev, show: false }));
      setOpen(false);
      focusInput();
    } else {
      setSale(null);
      setAlertState({
        show: true,
        text: "app.sale.validation.alert_msg_not_found",
        color: "bg-red-200",
      });
    }
  };

  const handleClick = async () => {
    const items = [];

    const allLines = sale?.["lines."] || [];
    const shopCategories = categoriesShop ?? [];
    const shopIds = shopCategories.map((c) => Number(c?.id ?? c));
    const filtered = allLines.filter((line) => {
      if (shopIds.length === 0) {
        return true;
      }
      const lineCategories = line?.["product."]?.categories || [];
      return lineCategories.some((cat) => shopIds.includes(Number(cat)));
    });

    for (const line of filtered) {
      const isProductMix =
        line?.["product."]?.["template."]?.["products_mix."]?.length > 0;
      if (isProductMix) {
        continue;
      }

      const isValidatable =
        line?.state_sale === "pending" ||
        line?.state_sale === "partial" ||
        line?.state_sale === "partially";
      if (!isValidatable) {
        continue;
      }

      const disponibles = line.quantity - (line.quantity_received ?? 0);
      const quantity = quantities[line.id] ?? disponibles;

      if (quantity > 0) {
        if (quantity > disponibles) {
          setAlertState({
            show: true,
            text: `La cantidad seleccionada (${quantity}) no puede ser mayor a las disponibles (${disponibles}) para el producto "${line?.["product."]?.name}"`,
            color: "bg-red-200",
          });
          return;
        }

        items.push({
          id: line.id,
          name: line?.["product."]?.name ?? "Producto",
          quantity,
        });
      }
    }

    if (items.length === 0) {
      setAlertState({
        show: true,
        text: "app.sale.validation.alert_msg_no_products_selected",
        color: "bg-yellow-100",
      });
      return;
    }

    setMarkedProducts(items);
    setModalView("confirm");
    setOpen(true);
  };

  async function handleConfirm() {
    const arrayProducts = markedProducts.map((item) => ({
      id: item.id,
      quantity_received: item.quantity,
    }));

    const _arg = {
      main_shop: sale.shop,
      shop: ctxActivity?.shop,
      quantity_received: 2,
      lines: arrayProducts,
      opportunity: sale.id,
      agent: ctxActivity?.agent?.id ?? null,
    };

    const { data, error } = await proxy.methodCall({
      model: "crm.opportunity",
      method: "get_products_by_shop",
      args: [_arg],
    });

    if (!error) {
      await handleQrSearch({ target: { value: sale?.number } });
      setAlertState({
        show: true,
        text: "app.sale.validation.alert_msg_success",
        color: "bg-green-100",
        icon: "check",
      });
      setOpen(false);
      setQuantities({});
      setMarkedProducts([]);
      focusInput();
    } else {
      setAlertState({
        show: true,
        text:
          typeof error === "string"
            ? error
            : (error?.error ?? "Error al confirmar ingresos"),
        color: "bg-red-200",
      });
    }
  }

  const handleResetDevice = async () => {
    await setStorage("saleActivity", null);
    await setStorage("ctxSession", { ...session, shop: null });
    setCtxActivity(null);
    setSale(null);
    setQuantities({});
    setMarkedProducts([]);
    setAlertState((prev) => ({ ...prev, show: false }));
  };

  const MethodSearch = () => (
    <div
      className="md:absolute h-fit w-fit fixed bottom-2 right-3 md:right-0 md:top-3 bg-yellow-500 rounded-full p-3 md:p-5 cursor-pointer z-20 border border-gray-900"
      onClick={() => {
        setModalView("scan");
        setOpen(true);
      }}
    >
      <QrCodeIcon className="size-5  md:size-8 fill-current" />
    </div>
  );

  const ContentScanQr = () => (
    <div className="overflow-hidden rounded-md">
      <JsQrReader onResult={(result) => handleQrSearch(result, true)} />
    </div>
  );

  function ViewConfirm() {
    return (
      <Fragment>
        {markedProducts.length > 0 ? (
          <div className="p-4 bg-gray-100 rounded-2xl shadow-md py-6">
            <h2 className="text-xl mb-4">
              Vas a descontar los siguientes productos:
            </h2>
            <div className="space-y-3 w-full">
              {markedProducts.map((item) => (
                <div
                  key={item.id}
                  className="text-gray-700 w-full p-4 bg-white shadow-md rounded-md flex justify-between"
                >
                  <span className="truncate flex space-x-2">
                    <TicketIcon className="w-5 h-5" /> <span>{item.name}</span>
                  </span>
                  <span className="flex">{item.quantity}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 flex space-x-5">
              <StdButton
                color="green"
                onClick={handleConfirm}
                size="md:w-fit !text-black"
                className=""
              >
                CONFIRMAR INGRESOS
              </StdButton>
            </div>
          </div>
        ) : (
          <div className="p-4 text-center">
            <p>No hay productos seleccionados para confirmar.</p>
            <StdButton
              className="mt-4 bg-gray-400 text-white"
              onClick={() => setOpen(false)}
              color="rose"
            >
              CERRAR
            </StdButton>
          </div>
        )}
      </Fragment>
    );
  }

  async function fetchCategoriesShop() {
    const { data, error } = await proxy.search(
      "sale.shop",
      [["id", "=", ctxActivity?.shop]],
      ["name", "product_categories"],
    );
    if (data) {
      setCategoriesShop(data[0]?.product_categories ?? []);
    } else {
      console.log(error?.error);
    }
  }

  function ItemData({ title, value }) {
    return (
      <div className="mb-2 text-xl">
        <label className="block font-semibold">{title}:</label>
        <span className="text-gray-800">{value}</span>
      </div>
    );
  }

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    fetchCategoriesShop();
    focusInput();
  }, [ctxActivity]);

  if (!ctxActivity?.sale_device) {
    return <SelectDevice handleCurrent={handleCurrentDevice} />;
  }

  return (
    <div className="w-full container relative md:mx-auto">
      <MethodSearch />

      <div className="fixed w-full md:w-fit mx-auto flex md:mt-5 md:mb-10 mb-5 items-center justify-center md:relative z-10">
        {!sale && (
          <div className="w-full animate-ping duration-150 bg-white h-full absolute left-0 top-0 rounded-xl overflow-hidden" />
        )}
        <SearchIcon className="w-8 md:w-10 fill-current absolute left-11 md:left-4 text-black z-10" />
        <input
          ref={inputRef}
          type="text"
          autoFocus
          onChange={handleQrSearch}
          placeholder="Escanea tu QR"
          className="md:py-3 px-4 pl-14 focus:outline-none rounded-md shadow-2xl relative bg-white focus:border focus:border-bluePresik-600  text-center h-16 text-4xl max-w-[350px]"
        />
      </div>

      {sale ? (
        <Fragment>
          <div className="mt-20 md:mt-0">
            <div className="md:grid md:grid-cols-2 md:gap-10 w-full items-start space-y-3 md:space-y-0 px-2 md:px-0">
              <div className="space-y-2 bg-white p-4 rounded-md shadow-sm">
                <h2 className="text-2xl font-bold mb-4">
                  Información de la venta
                </h2>

                <div className="md;grid md:grid-cols-3 md:gap-2 border-b border-gray-300 pb-2">
                  <ItemData title="Número de venta" value={sale?.number} />
                  <ItemData title="Fecha de venta" value={sale?.start_date} />
                  <ItemData title="Estado" value={sale?.state} />
                </div>
                <ItemData title="Fecha de ingreso" value={sale?.end_date} />
                <ItemData title="Cliente" value={sale?.["party."]?.name} />
                <ItemData
                  title="Numero de identificación"
                  value={Number(sale?.["party."]?.id_number).toLocaleString(
                    "es-CO",
                  )}
                />
                <ItemData title="Checkout ID" value={sale?.checkout} />
                <ItemData
                  title="Total"
                  value={formatNumberCo(sale?.total_amount)}
                />
                {sale.description != "web" && (
                  <p className="p-4 rounded-2xl shadow bg-green-100 border-green-300 text-green-900 w-fit">
                    {sale.description}
                  </p>
                )}
              </div>

              <div className="bg-white p-4 rounded-md shadow-sm relative">
                <h2 className="text-2xl font-bold mb-4">Productos</h2>
                <div className="space-y-2">
                  {sale?.["lines."]
                    ?.filter((line) => {
                      const lineCategories =
                        line?.["product."]?.categories || [];
                      const shopCategories = categoriesShop ?? [];
                      if (shopCategories.length === 0) {
                        return true;
                      }
                      const shopIds = shopCategories.map((c) =>
                        Number(c?.id ?? c),
                      );
                      return lineCategories.some((cat) =>
                        shopIds.includes(Number(cat)),
                      );
                    })
                    .map((line) => {
                      const isProductMix =
                        line?.["product."]?.["template."]?.["products_mix."]
                          ?.length > 0;
                      const disponibles =
                        line.quantity - (line.quantity_received ?? 0);

                      return !isProductMix ? (
                        <div
                          key={line.id}
                          className="md:flex items-center justify-between bg-gray-100 px-4 py-2 rounded-md"
                        >
                          <div className="md:flex flex-col md:w-2/3">
                            <p className="font-bold truncate max-w-[400px]">
                              {line?.["product."]?.name}
                            </p>
                            <div className="flex  md:flex-row flex-col space-x-2  justify-between w-full">
                              <div className="flex flex-col md:flex-row w-max font-bold">
                                <span className="pr-2 ">
                                  Disponibles:{" "}
                                  <span className="font-normal">
                                    {line?.quantity - line?.quantity_received}
                                  </span>
                                </span>
                                <span className="pr-2 ">
                                  Comprados:{" "}
                                  <span className="font-normal">
                                    {line?.quantity}
                                  </span>
                                </span>
                                <span>
                                  Consumidos:{" "}
                                  <span className="font-normal">
                                    {line?.quantity_received ?? 0}
                                  </span>
                                </span>
                                <span>
                                  Estado:{" "}
                                  <span className="font-normal">
                                    {line?.state_sale ?? 0}
                                  </span>
                                </span>
                              </div>
                            </div>
                            {line?.note && (
                              <div className="flex space-x-2">
                                <NoteIcon className="w-3" />
                                <p>Nota: {line?.note}</p>
                              </div>
                            )}
                          </div>
                          {sale.state === "succeeded" &&
                            (line?.state_sale === "pending" ||
                              line?.state_sale === "partially") &&
                            isToday.state && (
                              <div className="md:flex md:justify-end md:space-x-5">
                                <div className="max-w-[170px] md:max-w-[200px]">
                                  <IntegerField
                                    field={fieldQuantity}
                                    value={quantities[line.id] ?? disponibles}
                                    onChange={(v) => setQuantity(line.id, v)}
                                  />
                                </div>
                              </div>
                            )}
                        </div>
                      ) : (
                        <div>
                          <h2 className="font-bold truncate">
                            {line?.["product."]?.name}
                          </h2>
                        </div>
                      );
                    })}

                  <div className="flex space-x-2 md:my-10 mt-5 mb-10">
                    {sale.state === "succeeded" && isToday.state ? (
                      <StdButton
                        className="bg-black h-10 uppercase mt-1 rounded-md text-white px-3 hover:bg-blue-600 transition"
                        onClick={handleClick}
                        color="bluePresik"
                        size="md:w-fit"
                      >
                        MARCAR INGRESOS
                      </StdButton>
                    ) : (
                      <p className="p-4 rounded-2xl shadow bg-red-100 border-red-300 text-red-900">
                        {!isToday.state
                          ? isToday.mensaje
                          : "Aun no se han confirmado pagos para marcar ingresos"}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <PureModal
            open={openInfo}
            onClose={() => setOpenInfo(false)}
            backdrop
            width="w-[440px]"
            overflow="!overflow-hidden"
          >
            <InfoState
              state={sale.state}
              handleClose={() => setOpenInfo(false)}
              customMessage={!isToday.state ? isToday.mensaje : null}
            />
          </PureModal>
        </Fragment>
      ) : (
        <div className="w-full fixed h-full bg-black/30 left-0 top-0" />
      )}

      <PureModal
        open={open}
        onClose={() => setOpen(false)}
        backdrop
        width="w-[440px]"
        overflow="!overflow-hidden"
      >
        {modalView === "scan" ? <ContentScanQr /> : <ViewConfirm />}
      </PureModal>

      <Alert
        position="absolute bottom-3 left-0 right-0 z-20 max-w-2xl"
        onClose={() => setAlertState((prev) => ({ ...prev, show: false }))}
        autoRemove={true}
        {...alertState}
      />
    </div>
  );
};

export default ScreenSaleValidation;
